"use client"

import { useState, useCallback, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { KeySquare, Copy, Check, Wand2, AlertCircle, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"

type Algorithm = "HS256" | "HS384" | "HS512"

interface JwtHeader {
  alg: Algorithm
  typ: string
}

// Base64URL encoding (JWT-safe)
function base64UrlEncode(str: string): string {
  const base64 = btoa(str)
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/")
  while (base64.length % 4) {
    base64 += "="
  }
  return atob(base64)
}

// HMAC-SHA signing using Web Crypto API
async function sign(data: string, secret: string, algorithm: Algorithm): Promise<string> {
  const encoder = new TextEncoder()
  const keyData = encoder.encode(secret)
  const dataToSign = encoder.encode(data)

  const hashAlgo = algorithm === "HS256" ? "SHA-256" : algorithm === "HS384" ? "SHA-384" : "SHA-512"

  const key = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: hashAlgo },
    false,
    ["sign"]
  )

  const signature = await crypto.subtle.sign("HMAC", key, dataToSign)
  const signatureArray = new Uint8Array(signature)
  const signatureString = String.fromCharCode(...signatureArray)

  return base64UrlEncode(signatureString)
}

function createDefaultPayload(): string {
  const now = Math.floor(Date.now() / 1000)
  return JSON.stringify(
    {
      sub: "1234567890",
      name: "John Doe",
      iat: now,
      exp: now + 3600,
    },
    null,
    2
  )
}

// Verify HMAC signature
async function verify(token: string, secret: string): Promise<boolean> {
  const parts = token.split(".")
  if (parts.length !== 3) return false

  try {
    const header = JSON.parse(base64UrlDecode(parts[0])) as JwtHeader
    const dataToVerify = `${parts[0]}.${parts[1]}`
    const expectedSignature = await sign(dataToVerify, secret, header.alg)
    return expectedSignature === parts[2]
  } catch {
    return false
  }
}

export function JwtGenerator() {
  const [mode, setMode] = useState<"generate" | "decode">("generate")

  // Generate mode state
  const [algorithm, setAlgorithm] = useState<Algorithm>("HS256")
  const [secret, setSecret] = useState("")
  const [showSecret, setShowSecret] = useState(false)
  const [payload, setPayload] = useState("")
  const [generatedToken, setGeneratedToken] = useState("")

  // Decode mode state
  const [tokenToDecode, setTokenToDecode] = useState("")
  const [decodedHeader, setDecodedHeader] = useState("")
  const [decodedPayload, setDecodedPayload] = useState("")
  const [verifySecret, setVerifySecret] = useState("")
  const [isValid, setIsValid] = useState<boolean | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    queueMicrotask(() => {
      if (active) setPayload(createDefaultPayload())
    })
    return () => {
      active = false
    }
  }, [])

  // Generate JWT
  const generateJwt = useCallback(async () => {
    if (!secret.trim()) {
      toast.error("Please enter a secret key")
      return
    }

    setError(null)

    try {
      const payloadObj = JSON.parse(payload)

      const header: JwtHeader = {
        alg: algorithm,
        typ: "JWT",
      }

      const encodedHeader = base64UrlEncode(JSON.stringify(header))
      const encodedPayload = base64UrlEncode(JSON.stringify(payloadObj))
      const dataToSign = `${encodedHeader}.${encodedPayload}`

      const signature = await sign(dataToSign, secret, algorithm)
      const token = `${dataToSign}.${signature}`

      setGeneratedToken(token)
      toast.success("JWT generated successfully")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate JWT"
      setError(message)
      toast.error(message)
    }
  }, [algorithm, secret, payload])

  // Decode JWT
  const decodeJwt = useCallback(() => {
    if (!tokenToDecode.trim()) {
      toast.error("Please enter a JWT to decode")
      return
    }

    setError(null)
    setIsValid(null)

    try {
      const parts = tokenToDecode.split(".")
      if (parts.length !== 3) {
        throw new Error("Invalid JWT format (should have 3 parts)")
      }

      const header = JSON.parse(base64UrlDecode(parts[0]))
      const payload = JSON.parse(base64UrlDecode(parts[1]))

      setDecodedHeader(JSON.stringify(header, null, 2))
      setDecodedPayload(JSON.stringify(payload, null, 2))
      toast.success("JWT decoded successfully")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to decode JWT"
      setError(message)
      toast.error(message)
      setDecodedHeader("")
      setDecodedPayload("")
    }
  }, [tokenToDecode])

  // Verify signature
  const verifyJwt = useCallback(async () => {
    if (!tokenToDecode.trim() || !verifySecret.trim()) {
      toast.error("Please enter both JWT and secret")
      return
    }

    const valid = await verify(tokenToDecode, verifySecret)
    setIsValid(valid)
    toast[valid ? "success" : "error"](
      valid ? "Signature is valid!" : "Invalid signature"
    )
  }, [tokenToDecode, verifySecret])

  const handleTokenChange = (value: string) => {
    setTokenToDecode(value)
    setIsValid(null)

    if (!value.trim()) {
      setDecodedHeader("")
      setDecodedPayload("")
      setError(null)
      return
    }

    try {
      const parts = value.split(".")
      if (parts.length !== 3) return

      const header: unknown = JSON.parse(base64UrlDecode(parts[0]))
      const decodedTokenPayload: unknown = JSON.parse(base64UrlDecode(parts[1]))
      setDecodedHeader(JSON.stringify(header, null, 2))
      setDecodedPayload(JSON.stringify(decodedTokenPayload, null, 2))
      setError(null)
    } catch {
      setDecodedHeader("")
      setDecodedPayload("")
    }
  }

  const copy = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text)
    setCopied(key)
    toast.success("Copied to clipboard")
    setTimeout(() => setCopied(null), 2000)
  }, [])

  const addClaim = useCallback((claim: string) => {
    try {
      const obj = JSON.parse(payload)
      const now = Math.floor(Date.now() / 1000)

      switch (claim) {
        case "iat":
          obj.iat = now
          break
        case "exp":
          obj.exp = now + 3600
          break
        case "nbf":
          obj.nbf = now
          break
        case "jti":
          obj.jti = crypto.randomUUID()
          break
      }

      setPayload(JSON.stringify(obj, null, 2))
    } catch {
      toast.error("Invalid JSON payload")
    }
  }, [payload])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeySquare className="h-5 w-5" />
            JWT Generator & Decoder
          </CardTitle>
          <CardDescription>
            Create and sign JSON Web Tokens or decode and verify existing tokens.
            All processing happens in your browser.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={(v) => setMode(v as "generate" | "decode")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">Generate JWT</TabsTrigger>
              <TabsTrigger value="decode">Decode & Verify</TabsTrigger>
            </TabsList>

            {/* Generate Mode */}
            <TabsContent value="generate" className="space-y-4 mt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Algorithm</Label>
                  <Select value={algorithm} onValueChange={(v) => setAlgorithm(v as Algorithm)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HS256">HS256 (HMAC-SHA256)</SelectItem>
                      <SelectItem value="HS384">HS384 (HMAC-SHA384)</SelectItem>
                      <SelectItem value="HS512">HS512 (HMAC-SHA512)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Secret Key</Label>
                  <div className="relative">
                    <Input
                      type={showSecret ? "text" : "password"}
                      placeholder="your-256-bit-secret"
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Payload (JSON)</Label>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => addClaim("iat")}>
                      +iat
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => addClaim("exp")}>
                      +exp
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => addClaim("nbf")}>
                      +nbf
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => addClaim("jti")}>
                      +jti
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  className="min-h-[150px] font-mono text-sm"
                  placeholder='{"sub": "1234567890", "name": "John Doe"}'
                />
              </div>

              <Button onClick={generateJwt} className="w-full">
                <Wand2 className="h-4 w-4 mr-2" />
                Generate JWT
              </Button>

              {generatedToken && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Generated Token</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copy(generatedToken, "token")}
                    >
                      {copied === "token" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span className="ml-2">Copy</span>
                    </Button>
                  </div>
                  <div className="p-3 rounded-lg bg-muted font-mono text-xs break-all">
                    <span className="text-red-500">{generatedToken.split(".")[0]}</span>.
                    <span className="text-purple-500">{generatedToken.split(".")[1]}</span>.
                    <span className="text-blue-500">{generatedToken.split(".")[2]}</span>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Decode Mode */}
            <TabsContent value="decode" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>JWT Token</Label>
                <Textarea
                  value={tokenToDecode}
                  onChange={(e) => handleTokenChange(e.target.value)}
                  className="min-h-[100px] font-mono text-sm"
                  placeholder="Paste your JWT here..."
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={decodeJwt} className="flex-1">
                  Decode
                </Button>
              </div>

              {(decodedHeader || decodedPayload) && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-red-500">Header</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copy(decodedHeader, "header")}
                      >
                        {copied === "header" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    </div>
                    <pre className="p-3 rounded-lg bg-muted font-mono text-xs overflow-auto max-h-[200px]">
                      {decodedHeader}
                    </pre>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-purple-500">Payload</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copy(decodedPayload, "payload")}
                      >
                        {copied === "payload" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    </div>
                    <pre className="p-3 rounded-lg bg-muted font-mono text-xs overflow-auto max-h-[200px]">
                      {decodedPayload}
                    </pre>
                  </div>
                </div>
              )}

              {/* Verification */}
              {decodedPayload && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Verify Signature</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        type="password"
                        placeholder="Enter secret key to verify"
                        value={verifySecret}
                        onChange={(e) => {
                          setVerifySecret(e.target.value)
                          setIsValid(null)
                        }}
                        className="flex-1"
                      />
                      <Button onClick={verifyJwt}>Verify</Button>
                    </div>
                    {isValid !== null && (
                      <div
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                          isValid ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"
                        }`}
                      >
                        {isValid ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        {isValid ? "Signature verified!" : "Invalid signature"}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm mt-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Claims Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Standard JWT Claims</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">iss</code> - Issuer
            </div>
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">sub</code> - Subject
            </div>
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">aud</code> - Audience
            </div>
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">exp</code> - Expiration Time
            </div>
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">nbf</code> - Not Before
            </div>
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">iat</code> - Issued At
            </div>
            <div className="p-2 rounded bg-muted/50">
              <code className="font-bold">jti</code> - JWT ID
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
